import type { StepperIndicatorVariant } from './stepperIndicatorTypes'
import type { StepperOrientation, StepperSize, StepperStepProps } from './stepperTypes'

import { Box } from '../box'
import { styled, th, useStyleConfig, useTheme, vui } from '../core'
import { cs } from '../utils'
import { CLASS, CSS_VARS } from './stepperConsts'
import { useStepperContext } from './stepperContext'
import { StepperIndicator } from './stepperIndicator'

// ─── Base elements ────────────────────────────────────────────────────────────

const StepperBaseHorizontal = styled.divBox<{ $tailColor: string; $iconTailColor: string }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  ${CSS_VARS.tailColor}: ${p => p.$tailColor};
  ${CSS_VARS.iconTailColor}: ${p => p.$iconTailColor};
`

const StepperBaseVertical = styled.divBox<{ $tailColor: string; $vertTailWidth: string }>`
  display: flex;
  gap: var(${CSS_VARS.vertGap}, 8px);
  position: relative;
  width: 100%;
  ${CSS_VARS.tailColor}: ${p => p.$tailColor};
  ${CSS_VARS.vertTailMaxWidth}: ${p => p.$vertTailWidth};
`

const StepperTitleBox = styled.divBox`
  display: block;
  white-space: nowrap;
`

// ─── Tails ────────────────────────────────────────────────────────────────────

const StepperHorizTail = ({ isIconMode = false }: { isIconMode?: boolean }) => {
  const border = `1px solid var(${isIconMode ? CSS_VARS.iconTailColor : CSS_VARS.tailColor})`
  return (
    <Box
      borderBottom={border}
      className={CLASS.tail}
      h={`var(${CSS_VARS.horizTailHeight}, 16px)`}
      left="50%"
      position="absolute"
      w="100%"
    />
  )
}

const StepperVertTail = () => (
  <Box
    borderRight={`1px solid var(${CSS_VARS.tailColor})`}
    className={CLASS.tail}
    maxW={`var(${CSS_VARS.vertTailMaxWidth}, 16px)`}
    minH="100%"
    position="absolute"
    w="100%"
  />
)

// ─── StepperStep ──────────────────────────────────────────────────────────────

type StepProps = {
  icon?: StepperStepProps['icon']
  number?: number
  size: StepperSize
  description?: React.ReactNode
  title?: React.ReactNode
  className?: string
  variant?: StepperIndicatorVariant
  children?: React.ReactNode
  isIconMode: boolean
  ref?: React.Ref<HTMLDivElement>
  styles: ReturnType<typeof useStyleConfig>
  tailColor: string
  iconTailColor: string
}

const StepperStepVertical = ({
  icon,
  number,
  size,
  description,
  title,
  className,
  variant,
  children,
  isIconMode,
  ref,
  styles,
  tailColor: resolvedTailColor,
}: StepProps) => {
  const { title: titleStyles, desc: descStyles, indicatorWidth, titleColor, titleFontWeight, vertTailWidth } = styles
  const indicatorFlex = `0 0 ${indicatorWidth ?? '32px'}`

  return (
    <StepperBaseVertical
      $tailColor={resolvedTailColor}
      $vertTailWidth={vertTailWidth ?? '16px'}
      className={cs(CLASS.step, variant && `${CLASS.step}--${variant}`, className)}
      ref={ref}
    >
      <StepperVertTail />
      <Box flex={indicatorFlex} position="relative" zIndex={1}>
        <StepperIndicator icon={icon} number={number} size={size} variant={variant} />
      </Box>
      <Box flex="1" flexDirection="column">
        {!!title && (
          <StepperTitleBox className={CLASS.title} {...titleStyles} color={titleColor} fontWeight={titleFontWeight}>
            {title}
          </StepperTitleBox>
        )}
        {!!description && (
          <Box {...descStyles} display={'block'}>
            {description}
          </Box>
        )}
        {children}
      </Box>
    </StepperBaseVertical>
  )
}

const StepperStepHorizontal = ({
  icon,
  number,
  size,
  description,
  title,
  className,
  variant,
  children,
  isIconMode,
  ref,
  styles,
  tailColor: resolvedTailColor,
  iconTailColor: resolvedIconTailColor,
}: StepProps) => {
  const { title: titleStyles, desc: descStyles, titleColor, titleFontWeight } = styles

  return (
    <StepperBaseHorizontal
      $tailColor={resolvedTailColor}
      $iconTailColor={resolvedIconTailColor}
      className={cs(CLASS.step, variant && `${CLASS.step}--${variant}`, className)}
      ref={ref}
    >
      <StepperHorizTail isIconMode={isIconMode} />
      <Box display="block" position="relative" textAlign="center" zIndex={1}>
        <StepperIndicator icon={icon} number={number} size={size} variant={variant} />
      </Box>
      <Box display="block" textAlign="center">
        {!!title && (
          <StepperTitleBox
            className={CLASS.title}
            {...titleStyles}
            color={titleColor}
            fontWeight={titleFontWeight}
            textAlign="center"
          >
            {title}
          </StepperTitleBox>
        )}
        {!!description && (
          <Box display="block" textAlign="center" {...descStyles}>
            {description}
          </Box>
        )}
        {children}
      </Box>
    </StepperBaseHorizontal>
  )
}

export const StepperStep = vui<'div', StepperStepProps>(
  ({ icon, number, size: propSize = 'md', description, title, className, variant, children }, ref) => {
    const context = useStepperContext()
    const size = (context?.size || propSize) as StepperSize
    const orientation = (context?.orientation || 'horizontal') as StepperOrientation
    const isIconMode = Boolean(icon)
    const styles = useStyleConfig('Stepper', { size, variant })
    const theme = useTheme()
    const rc = (token: string) => th.color(token)({ theme }) as string
    const resolvedTailColor = rc(isIconMode ? styles.iconTailColor : styles.tailColor)
    const resolvedIconTailColor = rc(styles.iconTailColor)
    const stepProps = {
      icon,
      number,
      size,
      description,
      title,
      className,
      variant,
      children,
      isIconMode,
      ref,
      styles,
      tailColor: resolvedTailColor,
      iconTailColor: resolvedIconTailColor,
    }

    return orientation === 'vertical' ? (
      <StepperStepVertical {...stepProps} />
    ) : (
      <StepperStepHorizontal {...stepProps} />
    )
  },
)

StepperStep.displayName = 'StepperStep'

export default StepperStep
